import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { phoneRuleFor } from '../../lib/country';
import { hintFor, INDEPENDENT_WHO, STEP_HINT } from './copy';
import { firstName, MIN_PASSWORD_LENGTH } from './draft';
import { Named, Question, type StepProps } from './Question';

type ImportResult = { found: string[]; warnings: string[] };
type NameStepProps = StepProps & {
  onImport: (text: string) => Promise<ImportResult>;
  /** From Martín's invitation: no way out to the other signup, which would drop the free months. */
  invited?: boolean;
};

/** Step 1. The name, and a shortcut: the business's own website, or its Google, Degusta or OpenTable page. */
export function NameStep({ draft, update, next, error, attempt, onImport, invited }: NameStepProps) {
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleImport() {
    if (!importText.trim()) return;
    setImporting(true);
    setResult(await onImport(importText.trim()));
    setImporting(false);
  }

  const solo = draft.mode === 'independiente';
  return (
    <Question
      id="nombre"
      title={solo ? '¿Cómo te conocen tus clientes?' : '¿Cómo se llama tu negocio?'}
      hint={hintFor(draft.mode, 'nombre')}
      error={error}
      attempt={attempt}
      onSubmit={next}
      kicker={solo ? <SoloKicker /> : null}
      aside={invited ? undefined : <OtherSignup mode={draft.mode} />}
    >
      <input
        className="wiz-input"
        aria-labelledby="nombre-title"
        autoFocus
        autoComplete={solo ? 'name' : 'organization'}
        placeholder={solo ? 'Por ejemplo, Uñas por Carla' : 'Por ejemplo, Pan de la Casa'}
        value={draft.businessName}
        onChange={(event) => update({ businessName: event.target.value })}
      />
      {importOpen ? (
        <div className="wiz-import">
          <label htmlFor="wiz-import-text">Pega el enlace de tu página web, de Google, Degusta u OpenTable, o los datos que copiaste de ahí.</label>
          <textarea id="wiz-import-text" rows={2} value={importText} onChange={(event) => setImportText(event.target.value)} />
          <button type="button" className="btn small secondary" disabled={importing || !importText.trim()} onClick={() => void handleImport()}>
            {importing ? 'Buscando…' : 'Traer mis datos'}
          </button>
          {result ? <ImportSummary result={result} /> : null}
        </div>
      ) : (
        <button type="button" className="wiz-link" onClick={() => setImportOpen(true)}>
          ¿Tienes página web o estás en Google? Trae tus datos y te ahorras preguntas
        </button>
      )}
    </Question>
  );
}

/** The independent signup says who it is for before the first question. */
function SoloKicker() {
  return (
    <>
      <p className="wiz-kicker-label">Para independientes</p>
      <p className="wiz-kicker-who">{INDEPENDENT_WHO}</p>
    </>
  );
}

/** The way to the other signup, for anyone who landed on the wrong one. */
function OtherSignup({ mode }: { mode: 'negocio' | 'independiente' }) {
  if (mode === 'independiente') {
    return (
      <p>
        ¿Tienes un negocio con equipo o local? <Link to="/registro">Regístralo aquí</Link>
      </p>
    );
  }
  return (
    <p>
      ¿Trabajas por tu cuenta, sin empleados? <Link to="/registro/independiente">Regístrate como independiente</Link>
    </p>
  );
}

function ImportSummary({ result }: { result: ImportResult }) {
  return (
    <div className="wiz-import-result" role="status">
      {result.found.length > 0 ? <p>Listo, trajimos {joinSpanish(result.found)}.</p> : null}
      {result.warnings.map((warning) => (
        <p key={warning} className="wiz-import-warning">
          {warning}
        </p>
      ))}
    </div>
  );
}

/** "dirección, teléfono y horario". */
export function joinSpanish(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

export function OwnerStep({ draft, update, next, error, attempt }: StepProps) {
  return (
    <Question id="dueno" title="¿Y tú cómo te llamas?" hint={STEP_HINT.dueno} error={error} attempt={attempt} onSubmit={next}>
      <input
        className="wiz-input"
        aria-labelledby="dueno-title"
        autoFocus
        autoComplete="name"
        value={draft.ownerName}
        onChange={(event) => update({ ownerName: event.target.value })}
      />
    </Question>
  );
}

export function EmailStep({ draft, update, next, error, attempt }: StepProps) {
  const name = firstName(draft.ownerName);
  const title = name ? (
    <>
      Mucho gusto, <Named>{name}</Named>. ¿Cuál es tu correo?
    </>
  ) : (
    '¿Cuál es tu correo?'
  );
  return (
    <Question id="correo" title={title} hint={STEP_HINT.correo} error={error} attempt={attempt} onSubmit={next}>
      <input
        className="wiz-input"
        aria-labelledby="correo-title"
        autoFocus
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        placeholder="tu@correo.com"
        value={draft.email}
        onChange={(event) => update({ email: event.target.value })}
      />
    </Question>
  );
}

export function PhoneStep({ draft, update, next, error, attempt }: StepProps) {
  const rule = phoneRuleFor(draft.country);
  return (
    <Question
      id="telefono"
      title="¿A qué número te escriben tus clientes?"
      hint={hintFor(draft.mode, 'telefono')}
      error={error}
      attempt={attempt}
      onSubmit={next}
      onSkip={() => {
        update({ phone: '' });
        next();
      }}
    >
      <input
        className="wiz-input"
        aria-labelledby="telefono-title"
        autoFocus
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder={rule.placeholder}
        value={draft.phone}
        onChange={(event) => update({ phone: event.target.value })}
      />
    </Question>
  );
}

export function PasswordStep({ draft, update, next, error, attempt }: StepProps) {
  const [visible, setVisible] = useState(false);
  const filled = Math.min(draft.password.length / MIN_PASSWORD_LENGTH, 1);
  const ready = filled === 1;
  return (
    <Question id="clave" title="Crea una contraseña" hint={STEP_HINT.clave} error={error} attempt={attempt} onSubmit={next}>
      <div className="wiz-password">
        <input
          className="wiz-input"
          aria-labelledby="clave-title"
          autoFocus
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          value={draft.password}
          onChange={(event) => update({ password: event.target.value })}
        />
        <button type="button" className="wiz-reveal" aria-pressed={visible} onClick={() => setVisible(!visible)}>
          {visible ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>
      <div className="wiz-meter" data-ready={ready ? 'true' : undefined} aria-hidden="true">
        <span style={{ transform: `scaleX(${filled})` }} />
      </div>
      <p className="wiz-meter-label" aria-live="polite">
        {ready ? 'Perfecta.' : `${draft.password.length} de ${MIN_PASSWORD_LENGTH} caracteres`}
      </p>
    </Question>
  );
}
